// src/auth/RoleSwitcher.jsx
//
// Renders a plain role label (single role) or a dropdown (multiple roles).
// Dropdown uses position:fixed so it escapes ALL overflow:hidden containers.
// On role switch: clears session, re-persists chosen role, hard-reloads app.

import { useState, useEffect, useRef, useCallback } from "react";

/* ── Session keys — must match RequireAuth.jsx ── */
const SS = sessionStorage;

// ONLY clear role-related keys on role switch.
// Identity, lookup cache, and session timing keys MUST be preserved
// so RequireAuth does not treat the reload as a new login and trigger
// a full MSAL logout/redirect.
const ROLE_KEYS_TO_CLEAR = [
    "tb_role",
    "tb_role_raw",
    "tb_role_relogin_once",
    "tb_role_effective",
    "tb_role_user_selected",
    // Profile fields that are role-dependent and will be re-resolved
    // from the preserved Hinge lookup cache on next boot
    "tb_personType",
    "tb_dtvRoleName",
    "tb_isFte",
    "tb_directReports",
    "tb_supplierName",
    "tb_vendorId",
    // DO NOT clear: tb_name, tb_email, tb_dtvid, tb_attuid,
    //   tb_lookup*, tb_groups, tb_login_at, tb_last_activity,
    //   tb_login_kicked, tb_hinge_person_list
    // These keep the user authenticated across the role switch reload.
];

const K = {
    role: "tb_role",
    availableRoles: "tb_available_roles",
};

const ROLE_PRIORITY = [
    "DTV OPS Admin",
    "Employee Supplier Admin",
    "Employee Sponsor",
    "Nominator",
    "FTE Nominator",
    "CP Channel Delegate",
    "Read Only",
];

function readAvailableRoles() {
    try {
        const raw = SS.getItem(K.availableRoles);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) return [];
        return [...parsed].sort((a, b) => {
            const ai = ROLE_PRIORITY.indexOf(a);
            const bi = ROLE_PRIORITY.indexOf(b);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
    } catch {
        return [];
    }
}

function readActiveRole() {
    return SS.getItem(K.role) || "";
}

function switchRole(newRole, availableRoles) {
    // Snapshot the available-roles list before clearing anything
    const listSnapshot = JSON.stringify(availableRoles);

    // Only wipe role-specific keys — identity/lookup/session keys stay
    // intact so RequireAuth sees a valid session on reload, not a logout
    ROLE_KEYS_TO_CLEAR.forEach((k) => {
        try { SS.removeItem(k); } catch { /* ignore */ }
    });

    // Re-persist the new role and the full available list.
    // tb_role_user_selected tells RequireAuth bootstrap to honour this
    // choice on reload instead of resetting to the highest-priority role.
    try {
        SS.setItem(K.role, newRole);
        SS.setItem(K.availableRoles, listSnapshot);
        SS.setItem("tb_role_user_selected", newRole);
    } catch { /* ignore */ }

    // Hard reload — app re-mounts under the new role with same auth session
    window.location.replace("/");
}

export default function RoleSwitcher() {
    const [availableRoles, setAvailableRoles] = useState(() => readAvailableRoles());
    const [activeRole, setActiveRole] = useState(() => readActiveRole());
    const [open, setOpen] = useState(false);

    // For fixed-position dropdown — track trigger button position
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

    // Sync session → state (RequireAuth writes after async lookup)
    useEffect(() => {
        const sync = () => {
            const roles = readAvailableRoles();
            const role = readActiveRole();
            setAvailableRoles(roles);
            setActiveRole(role);
        };
        sync();

        const onStorage = (e) => {
            if (e.storageArea === sessionStorage &&
                (e.key === K.role || e.key === K.availableRoles)) {
                sync();
            }
        };
        window.addEventListener("storage", onStorage);

        // Poll until session settles (RequireAuth writes after async lookup)
        const poll = setInterval(sync, 250);
        const stop = setTimeout(() => clearInterval(poll), 15000);

        return () => {
            window.removeEventListener("storage", onStorage);
            clearInterval(poll);
            clearTimeout(stop);
        };
    }, []);

    // Compute fixed position of dropdown from trigger button rect
    const openDropdown = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropPos({
                top: rect.bottom + 4,
                left: rect.right,   // right-align: we'll translate left in CSS
                width: Math.max(rect.width, 220),
            });
        }
        setOpen(true);
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            const insideTrigger = triggerRef.current?.contains(e.target);
            const insideDropdown = dropdownRef.current?.contains(e.target);
            if (!insideTrigger && !insideDropdown) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Reposition on scroll/resize while open
    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setDropPos({
                    top: rect.bottom + 4,
                    left: rect.right,
                    width: Math.max(rect.width, 220),
                });
            }
        };
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open]);

    // Single role — plain text label, no dropdown
    if (availableRoles.length <= 1) {
        return activeRole
            ? <span style={styles.singleLabel}>{activeRole}</span>
            : null;
    }

    return (
        <>
            {/* Trigger button */}
            <button
                ref={triggerRef}
                style={styles.trigger}
                onClick={() => open ? setOpen(false) : openDropdown()}
                aria-haspopup="listbox"
                aria-expanded={open}
                title="Switch role"
                type="button"
            >
                <span style={styles.triggerText}>{activeRole || "Select role"}</span>
                <span style={{
                    ...styles.chevron,
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                }}>▾</span>
            </button>

            {/* Dropdown — rendered via fixed position to escape all overflow:hidden */}
            {open && (
                <ul
                    ref={dropdownRef}
                    role="listbox"
                    style={{
                        ...styles.dropdown,
                        position: "fixed",
                        top: dropPos.top,
                        left: dropPos.left,
                        minWidth: dropPos.width,
                        transform: "translateX(-100%)",  // right-align to trigger
                    }}
                >
                    {availableRoles.map((r) => (
                        <li
                            key={r}
                            role="option"
                            aria-selected={r === activeRole}
                            style={{
                                ...styles.option,
                                ...(r === activeRole ? styles.optionActive : {}),
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault(); // prevent blur before click registers
                                if (r === activeRole) { setOpen(false); return; }
                                setOpen(false);
                                switchRole(r, availableRoles);
                            }}
                        >
                            {r === activeRole && <span style={styles.check}>✓ </span>}
                            {r}
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

const styles = {
    singleLabel: {
        fontSize: 11,
        color: "#64748b",
        fontWeight: 400,
    },
    trigger: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "none",
        border: "1px solid #d1d5db",
        borderRadius: 5,
        padding: "2px 7px",
        fontSize: 11,
        fontWeight: 500,
        color: "#374151",
        cursor: "pointer",
        whiteSpace: "nowrap",
        lineHeight: 1.4,
        marginTop: 1,
    },
    triggerText: {
        maxWidth: 180,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    chevron: {
        fontSize: 9,
        lineHeight: 1,
        transition: "transform 0.15s",
        flexShrink: 0,
    },
    dropdown: {
        background: "#ffffff",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        padding: "4px 0",
        listStyle: "none",
        margin: 0,
        zIndex: 99999,   // above everything
    },
    option: {
        padding: "8px 16px",
        fontSize: 13,
        cursor: "pointer",
        color: "#374151",
        whiteSpace: "nowrap",
    },
    optionActive: {
        background: "#eff6ff",
        color: "#1d4ed8",
        fontWeight: 600,
    },
    check: {
        color: "#1d4ed8",
        fontWeight: 700,
    },
};
